import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { parseValidationErrors } from '../../utils/validationErrors';

const API_URL = process.env.REACT_APP_API_BASE_URL + '/api/auth/login/';
const SIGNUP_URL = process.env.REACT_APP_API_BASE_URL + '/api/auth/signup/';
const ONBOARDING_STAGE_URL = process.env.REACT_APP_API_BASE_URL + '/api/onboarding/stage/';

export const normalizeFeatureCodes = (features = []) => {
  if (!Array.isArray(features)) return [];
  return features
    .map((feature) => {
      if (!feature) return null;

      if (typeof feature === 'string') {
        const normalizedCode = feature.trim().toUpperCase();
        return {
          code: normalizedCode,
          name: feature,
        };
      }

      const normalizedCode = typeof feature.code === 'string'
        ? feature.code.trim().toUpperCase()
        : feature.code;

      return {
        ...feature,
        code: normalizedCode,
      };
    })
    .filter(Boolean);
};

const persistFeatures = (features = []) => {
  localStorage.setItem('features', JSON.stringify(features));
};

const clearStoredAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh');
  localStorage.removeItem('user_id');
  localStorage.removeItem('user_type');
  localStorage.removeItem('features');
  localStorage.removeItem('brand_onboarding_stage');
};

const createEmptyOnboardingState = () => ({
  stage: null,
  status: null,
  deadline: null,
  time_remaining_hours: null,
  loading: false,
  error: null,
});

const applyLogoutState = (state) => {
  state.token = null;
  state.refresh = null;
  state.user_id = null;
  state.user_type = null;
  state.features = [];
  state.brand_onboarding_stage = null;
  state.onboarding = createEmptyOnboardingState();
};

export const logoutUser = createAsyncThunk(
  'user/logoutUser',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { user } = getState();
      const refreshToken = user.refresh;
      
      if (refreshToken) {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/logout/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            refresh: refreshToken,
          }),
        });
        
        if (!response.ok) {
          console.warn('Logout API call failed, but clearing local tokens anyway');
        } else {
          console.log('Logout API call successful');
        }
      }
      
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  }
);

export const loginUser = createAsyncThunk(
  'user/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          secret: process.env.REACT_APP_LOGIN_SECRET,
          username: credentials.username,
          password: credentials.password,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || errorData.message || 'Invalid credentials';
        throw new Error(errorMessage);
      }
      const data = await response.json();
      // Save tokens and user info to localStorage - NEW API FORMAT
      localStorage.setItem('token', data.token);
      localStorage.setItem('refresh', data.refresh);
      localStorage.setItem('user_id', data.user_id);
      localStorage.setItem('user_type', data.user_type);
      const normalizedFeatures = normalizeFeatureCodes(data.features || []);
      persistFeatures(normalizedFeatures);
      data.features = normalizedFeatures;
      if (data.brand_onboarding_stage !== undefined && data.brand_onboarding_stage !== null) {
        localStorage.setItem('brand_onboarding_stage', data.brand_onboarding_stage);
      }
      return data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const checkOnboardingStage = createAsyncThunk(
  'user/checkOnboardingStage',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { user } = getState();
      // Only call onboarding-stage API for appuser (brand users)
      if (user.user_type !== 'USER') {
        return {};
      }
      const response = await fetch(ONBOARDING_STAGE_URL, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.status === 401 || response.status === 403) {
        clearStoredAuth();
        const message = response.status === 401
          ? 'Session expired. Please log in again.'
          : 'Your access has changed. Please log in again.';
        return rejectWithValue({
          status: response.status,
          message,
          shouldLogout: true,
        });
      }
      if (!response.ok) throw new Error('Failed to check onboarding stage');
      const data = await response.json();
      // NEW API FORMAT: stage, status, deadline, time_remaining_hours
      // If onboarding is complete, fetch fresh user data to get updated features
      if (data.stage === 'completed') {
        console.log('checkOnboardingStage: Onboarding complete, fetching fresh user data...');

        try {
          const userResponse = await fetch(process.env.REACT_APP_API_BASE_URL + '/api/auth/users/', {
            headers: {
              'Authorization': `Bearer ${user.token}`,
              'Content-Type': 'application/json',
            },
          });

          if (userResponse.ok) {
            const userData = await userResponse.json();
            console.log('checkOnboardingStage: Fresh user data:', userData);

            // NEW API FORMAT: userData.user.accessible_features
            let features = [];
            if (userData.user && userData.user.accessible_features) {
              features = normalizeFeatureCodes(userData.user.accessible_features);
              console.log('checkOnboardingStage: Updated features:', features);

              // Update localStorage with fresh features
              persistFeatures(features);

              // Include features in the returned data
              data.updated_features = features;
            }
          }
        } catch (userError) {
          console.warn('checkOnboardingStage: Failed to fetch fresh user data:', userError);
        }
      }

      return data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const signupUser = createAsyncThunk(
  'user/signupUser',
  async (signupData, { rejectWithValue }) => {
    try {
      const response = await fetch(SIGNUP_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(signupData),
      });
      const data = await response.json();
      if (!response.ok) {
        // The server explains exactly which fields are wrong and why, so pass
        // that through rather than collapsing everything into one generic
        // "Invalid signup data" message.
        const parsed = parseValidationErrors(
          { data, status: response.status },
          'Signup failed. Please try again.'
        );
        return rejectWithValue({
          message: parsed.message,
          fieldErrors: parsed.fieldErrors,
          issues: parsed.issues,
          count: parsed.count,
        });
      }

      // Handle auto-login response
      if (data.token && data.refresh) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('refresh', data.refresh);
        localStorage.setItem('user_id', data.user_id);
        localStorage.setItem('user_type', data.user_type);
        
        const normalizedFeatures = normalizeFeatureCodes(data.features || []);
        localStorage.setItem('features', JSON.stringify(normalizedFeatures));
        
        if (data.brand_onboarding_stage !== undefined && data.brand_onboarding_stage !== null) {
          localStorage.setItem('brand_onboarding_stage', data.brand_onboarding_stage);
        }
      }
      
      return data;
    } catch (err) {
      return rejectWithValue({
        message: err.message || 'Signup failed. Please try again.',
        fieldErrors: {},
        issues: [],
        count: 0,
      });
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState: {
    token: localStorage.getItem('token') || null,
    refresh: localStorage.getItem('refresh') || null,
    user_id: localStorage.getItem('user_id') || null,
    user_type: localStorage.getItem('user_type') || null,
    features: localStorage.getItem('features') ? normalizeFeatureCodes(JSON.parse(localStorage.getItem('features'))) : [],
    brand_onboarding_stage: localStorage.getItem('brand_onboarding_stage') || null,
    loading: false,
    error: null,
    // Per-field detail from the last rejected signup, so the form can show the
    // message underneath the offending input.
    signupFieldErrors: {},
    signupIssues: [],
    onboarding: createEmptyOnboardingState(),
  },
  reducers: {
    logout(state) {
      applyLogoutState(state);
      clearStoredAuth();
    },
    setFeatures(state, action) {
      const normalizedFeatures = normalizeFeatureCodes(action.payload || []);
      state.features = normalizedFeatures;
      persistFeatures(normalizedFeatures);
    },
    setUser(state, action) {
      state.token = action.payload.token;
      state.refresh = action.payload.refresh;
      state.user_id = action.payload.user_id;
      state.user_type = action.payload.user_type;
      if (action.payload.features !== undefined) {
        const normalizedFeatures = normalizeFeatureCodes(action.payload.features || []);
        state.features = normalizedFeatures;
        persistFeatures(normalizedFeatures);
      }
      if (action.payload.brand_onboarding_stage !== undefined) {
        state.brand_onboarding_stage = action.payload.brand_onboarding_stage;
      }
    },
    clearOnboardingError(state) {
      state.onboarding.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.refresh = action.payload.refresh;
        state.user_id = action.payload.user_id;
        state.user_type = action.payload.user_type;
        state.features = normalizeFeatureCodes(action.payload.features || []);
        if (action.payload.brand_onboarding_stage !== undefined) {
          state.brand_onboarding_stage = action.payload.brand_onboarding_stage;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.signupFieldErrors = {};
        state.signupIssues = [];
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.loading = false;
        // Set user state from the response data after successful signup
        state.token = action.payload.token;
        state.refresh = action.payload.refresh;
        state.user_id = action.payload.user_id;
        state.user_type = action.payload.user_type;
        state.features = normalizeFeatureCodes(action.payload.features || []);
        if (action.payload.brand_onboarding_stage !== undefined) {
          state.brand_onboarding_stage = action.payload.brand_onboarding_stage;
        }
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        // `error` stays a plain string so everything already reading it keeps
        // working; the per-field detail lives alongside it.
        state.error = (payload && payload.message) || action.error?.message || 'Signup failed. Please try again.';
        state.signupFieldErrors = (payload && payload.fieldErrors) || {};
        state.signupIssues = (payload && payload.issues) || [];
      })
      .addCase(checkOnboardingStage.pending, (state) => {
        state.onboarding.loading = true;
        state.onboarding.error = null;
      })
      .addCase(checkOnboardingStage.fulfilled, (state, action) => {
        state.onboarding.loading = false;
        // NEW API FORMAT: stage, status, deadline, time_remaining_hours
        state.onboarding.stage = action.payload.stage;
        state.onboarding.status = action.payload.status;
        state.onboarding.deadline = action.payload.deadline;
        state.onboarding.time_remaining_hours = action.payload.time_remaining_hours;

        // Update features if they were refreshed
        if (action.payload.updated_features) {
          console.log('checkOnboardingStage reducer: Updating features:', action.payload.updated_features);
          const normalizedFeatures = normalizeFeatureCodes(action.payload.updated_features);
          state.features = normalizedFeatures;
          persistFeatures(normalizedFeatures);
        }
      })
      .addCase(checkOnboardingStage.rejected, (state, action) => {
        state.onboarding.loading = false;
        const errorMessage = typeof action.payload === 'string'
          ? action.payload
          : action.payload?.message;
        state.onboarding.error = errorMessage || action.error?.message || null;
        if (action.payload?.shouldLogout) {
          applyLogoutState(state);
        }
      })
      .addCase(logoutUser.fulfilled, (state) => {
        applyLogoutState(state);
        clearStoredAuth();
      })
      .addCase(logoutUser.rejected, (state) => {
        applyLogoutState(state);
        clearStoredAuth();
      });
  },
});

export const { logout, setUser, setFeatures, clearOnboardingError } = userSlice.actions;
export default userSlice.reducer;
